import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Register.css';

function Register() {
  const [step, setStep] = useState(1); // 1: 이메일 입력, 2: 인증 코드 입력, 3: 회원정보 입력
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    password: '',
    confirmPassword: '',
    username: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();

  // 타이머 시작
  React.useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  // Step 1: 이메일 인증 코드 전송
  const handleSendVerification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email || !formData.email.includes('@')) {
      setError('유효한 이메일 주소를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/email/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '인증 코드 전송에 실패했습니다.');
      }

      setSuccess('인증 코드가 이메일로 전송되었습니다. (10분간 유효)');
      setStep(2);
      setTimer(600); // 10분 타이머
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: 인증 코드 확인
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      setError('6자리 인증 코드를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/email/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          code: formData.verificationCode
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '인증 코드가 올바르지 않습니다.');
      }

      setSuccess('이메일 인증이 완료되었습니다!');
      setTimer(0);
      setTimeout(() => {
        setStep(3);
        setSuccess('');
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: 회원가입 완료
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // 비밀번호 확인
    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (formData.password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          username: formData.username
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '회원가입에 실패했습니다.');
      }

      // 토큰과 사용자 정보 저장
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // 메인 페이지로 이동
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 타이머 포맷팅
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="register-container">
      <div className="register-box">
        <h2>회원가입</h2>
        
        {/* 진행 단계 표시 */}
        <div className="progress-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">이메일</div>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">인증</div>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">정보입력</div>
          </div>
        </div>

        {/* Step 1: 이메일 입력 */}
        {step === 1 && (
          <form onSubmit={handleSendVerification}>
            <div className="form-group">
              <label htmlFor="email">이메일</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="이메일을 입력하세요"
                required
                autoFocus
              />
              <p className="form-hint">인증 코드가 이메일로 전송됩니다.</p>
            </div>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <button type="submit" className="register-button" disabled={loading}>
              {loading ? '전송 중...' : '인증 코드 전송'}
            </button>
          </form>
        )}

        {/* Step 2: 인증 코드 입력 */}
        {step === 2 && (
          <form onSubmit={handleVerifyCode}>
            <div className="email-display">
              <span className="email-label">인증 이메일:</span>
              <span className="email-value">{formData.email}</span>
              <button 
                type="button" 
                className="change-email-btn"
                onClick={() => {
                  setStep(1);
                  setTimer(0);
                  setFormData({ ...formData, verificationCode: '' });
                }}
              >
                변경
              </button>
            </div>
            <div className="form-group">
              <label htmlFor="verificationCode">
                인증 코드
                {timer > 0 && (
                  <span className="timer"> ({formatTime(timer)})</span>
                )}
              </label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={handleChange}
                placeholder="6자리 인증 코드"
                maxLength="6"
                required
                autoFocus
              />
              <p className="form-hint">이메일로 전송된 6자리 코드를 입력하세요.</p>
            </div>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <button type="submit" className="register-button" disabled={loading}>
              {loading ? '확인 중...' : '인증 확인'}
            </button>
            <button 
              type="button" 
              className="resend-button"
              onClick={handleSendVerification}
              disabled={loading || timer > 540}
            >
              인증 코드 재전송
            </button>
          </form>
        )}

        {/* Step 3: 회원 정보 입력 */}
        {step === 3 && (
          <form onSubmit={handleRegister}>
            <div className="email-display">
              <span className="email-label">이메일:</span>
              <span className="email-value verified">{formData.email} ✓</span>
            </div>
            <div className="form-group">
              <label htmlFor="username">이름</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="이름을 입력하세요"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">비밀번호</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="비밀번호를 입력하세요 (최소 6자)"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">비밀번호 확인</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="비밀번호를 다시 입력하세요"
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" className="register-button" disabled={loading}>
              {loading ? '가입 중...' : '회원가입 완료'}
            </button>
          </form>
        )}

        <div className="register-footer">
          <p>
            이미 계정이 있으신가요? <Link to="/login">로그인</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
